import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { userStorageFolderService } from '../../../services/userStorageFolderService';
import { bucketConfigService } from '../../../services/bucketConfigService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import useToast from '../../../hooks/useToast';

const StorageRequest = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [folders, setFolders] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    bucket_id: '',
    folder_name: ''
  });

  // Load storage folders and buckets
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const [foldersData, bucketsData] = await Promise.all([
          userStorageFolderService?.getAll(),
          bucketConfigService?.getAll()
        ]);
        
        setFolders(foldersData);
        setBuckets(bucketsData);
      } catch (error) {
        console.error('Error loading data:', error);
        showToast('Failed to load storage folders', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Subscribe to real-time changes
  useEffect(() => {
    if (!user) return;

    const channel = userStorageFolderService?.subscribeToChanges(user?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        setFolders(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setFolders(prev => prev?.map(folder => 
          folder?.id === payload?.new?.id ? payload?.new : folder
        ));
      } else if (payload?.eventType === 'DELETE') {
        setFolders(prev => prev?.filter(folder => folder?.id !== payload?.old?.id));
      }
    });

    return () => {
      userStorageFolderService?.unsubscribeFromChanges(channel);
    };
  }, [user]);

  const handleCreateFolder = async (e) => {
    e?.preventDefault();
    
    if (!formData?.bucket_id || !formData?.folder_name) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      setCreating(true);
      
      console.log('📁 Requesting storage folder creation...', {
        bucketId: formData?.bucket_id,
        folderName: formData?.folder_name
      });
      
      await userStorageFolderService?.create(formData);
      
      showToast('Storage folder created successfully in database and S3', 'success');
      setShowCreateForm(false);
      setFormData({ bucket_id: '', folder_name: '' });
      
      // Reload folders to show the newly created one
      const foldersData = await userStorageFolderService?.getAll();
      setFolders(foldersData);
      
    } catch (error) {
      console.error('Error creating folder:', error);
      showToast(error?.message || 'Failed to create storage folder', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleActivateFolder = async (folderId) => {
    try {
      console.log('🔄 Activating folder...', { folderId });
      
      await userStorageFolderService?.setActiveFolder(folderId);
      showToast('Folder activated successfully - all file operations will now use this folder', 'success');
      
      // Reload folders to reflect the activation status change
      const foldersData = await userStorageFolderService?.getAll();
      setFolders(foldersData);
      
    } catch (error) {
      console.error('Error activating folder:', error);
      showToast(error?.message || 'Failed to activate folder', 'error');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this storage folder? This will also delete all files in the folder from S3.')) {
      return;
    }

    try {
      console.log('🗑️ Deleting folder...', { folderId });
      
      await userStorageFolderService?.delete(folderId);
      showToast('Storage folder and its contents deleted successfully', 'success');
      
      // Reload folders to remove the deleted one from the list
      const foldersData = await userStorageFolderService?.getAll();
      setFolders(foldersData);
      
    } catch (error) {
      console.error('Error deleting folder:', error);
      showToast(error?.message || 'Failed to delete folder', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'inactive': return 'XCircle';
      default: return 'Circle';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Storage Folders</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Request and manage user-specific storage folders in your S3 buckets
          </p>
        </div>
        <Button
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowCreateForm(true)}
        >
          Request Storage
        </Button>
      </div>
      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="text-md font-semibold text-foreground mb-4">Request New Storage Folder</h4>
          <form onSubmit={handleCreateFolder} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Bucket <span className="text-destructive">*</span>
              </label>
              <select
                value={formData?.bucket_id}
                onChange={(e) => setFormData({ ...formData, bucket_id: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                required
              >
                <option value="">Choose a bucket...</option>
                {buckets?.map((bucket) => (
                  <option key={bucket?.id} value={bucket?.id}>
                    {bucket?.bucketName} ({bucket?.bucketRegion})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Folder Name <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                value={formData?.folder_name}
                onChange={(e) => setFormData({ ...formData, folder_name: e?.target?.value })}
                placeholder="Enter folder name (e.g., my-storage)"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                A folder will be created as: username/folder-name
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={creating}
                iconName={creating ? 'Loader' : 'Check'}
                iconPosition="left"
              >
                {creating ? 'Creating...' : 'Create Folder'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ bucket_id: '', folder_name: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
      {/* Folders List */}
      {folders?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="FolderPlus" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Storage Folders</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Request your first storage folder to get started
          </p>
          <Button
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowCreateForm(true)}
          >
            Request Storage
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {folders?.map((folder) => (
            <div
              key={folder?.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="Folder" size={20} className="text-primary" />
                    <h4 className="text-lg font-semibold text-foreground">
                      {folder?.folder_name}
                    </h4>
                    {folder?.is_active && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        Active
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="HardDrive" size={16} />
                      <span>
                        {folder?.bucket_configurations?.bucket_name} 
                        ({folder?.bucket_configurations?.bucket_region})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="FolderTree" size={16} />
                      <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                        {folder?.folder_path}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name={getStatusIcon(folder?.status)} size={16} />
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(folder?.status)}`}>
                        {folder?.status}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 text-xs text-muted-foreground">
                    Created: {new Date(folder?.created_at)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>

                <div className="flex gap-2">
                  {!folder?.is_active && folder?.status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => handleActivateFolder(folder?.id)}
                      iconName="Power"
                    >
                      Activate
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteFolder(folder?.id)}
                    iconName="Trash2"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-2">How it works:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Request a storage folder for a specific S3 bucket</li>
              <li>Once created, activate the folder to make it your active storage location</li>
              <li>All uploads, downloads, and file operations will be scoped to your active folder</li>
              <li>Only one folder can be active at a time</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageRequest;